import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { ExpandMore } from '@material-ui/icons';
import clsx from 'clsx';

import { Paper } from '../paper/Paper';
import { AccordionDetailsStyled, AccordionStyled, AccordionSummaryStyled, useStyles } from './Accordion.styles';
import { AccordionProps } from './Accordion.types';

export const Accordion = memo(
  ({
    expanded,
    defaultExpanded,
    children,
    header,
    paperElevation,
    separator = 'dashed',
    expandIcon,
    expandIconColor,
    hideToggle,
    hideSeparator,
    noContentMargin,
    disabled,
    className,
    accordionClasses,
    onChange,
    ...rest
  }: AccordionProps) => {
    const styles = useStyles();

    const defaultValue = useMemo(() => {
      if (typeof expanded !== 'undefined') {
        return expanded;
      }

      if (typeof defaultExpanded !== 'undefined') {
        return defaultExpanded;
      }

      return false;
    }, [defaultExpanded, expanded]);

    const [isExpanded, setExpanded] = useState(defaultValue);

    const handleChange = useCallback<NonNullable<AccordionProps['onChange']>>(
      (event, expanded) => {
        if (onChange) {
          onChange(event, expanded);
        } else {
          setExpanded(expanded);
        }
      },
      [onChange],
    );

    useEffect(() => {
      setExpanded(defaultValue);
    }, [defaultValue]);

    return (
      <Paper variant={paperElevation ? 'elevation' : 'outlined'} elevation={paperElevation} className={className}>
        <AccordionStyled
          expanded={isExpanded}
          disabled={disabled}
          classes={accordionClasses?.accordion}
          onChange={handleChange}
          {...rest}
        >
          <AccordionSummaryStyled
            id="accordion-summary"
            aria-controls="accordion-content"
            expandIcon={!hideToggle && (expandIcon ?? <ExpandMore color={expandIconColor ?? 'primary'} />)}
            classes={accordionClasses?.accordionSummary}
          >
            {header}
          </AccordionSummaryStyled>

          <AccordionDetailsStyled
            classes={accordionClasses?.accordionDetails}
            className={clsx(!hideSeparator && separator && separator)}
          >
            <div className={clsx(styles.contentRoot, noContentMargin && 'noMargin', accordionClasses?.contentRoot)}>
              {children}
            </div>
          </AccordionDetailsStyled>
        </AccordionStyled>
      </Paper>
    );
  },
);
